import { Button, FormControl, HorizontalStack, Input, Modal, Text, Textarea } from '@lego/klik-ui';
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiErrorDetail } from '@/hooks/api';
import { useAddApplication } from '@/hooks/application';
import { useWorkspace } from '@/hooks/workspace';
import { paths } from '@/utility/global_paths';
import { Ag2AppIdSelect } from './Ag2AppIdSelect';

const NAME_MIN_LENGTH = 5;
const NAME_MAX_LENGTH = 100;
const DUPLICATE_NAME_PATTERN = /Key \(name, workspace_id\)=\((.+?),/;

function formatApiError(detail: string): string {
  const match = DUPLICATE_NAME_PATTERN.exec(detail);
  if (match) return `An application named '${match[1]}' already exists in this workspace`;
  return detail;
}

type Props = {
  workspaceId: number;
  isOpen: boolean;
  onClose: () => void;
};

export const CreateApplicationDialog = ({ workspaceId, isOpen, onClose }: Props) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ag2AppId, setAg2AppId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const navigate = useNavigate();
  const { data: workspaceResponse, isLoading: isWorkspaceLoading } = useWorkspace(workspaceId);

  const lastErrorDetail = useRef<string | undefined>(undefined);
  const onApiError = useCallback((err?: unknown) => {
    lastErrorDetail.current = getApiErrorDetail(err);
  }, []);
  const addApplication = useAddApplication(workspaceId, onApiError);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setAg2AppId(null);
      setError(undefined);
    }
  }, [isOpen]);

  const trimmedName = name.trim();
  const nameIsTooShort = trimmedName.length > 0 && trimmedName.length < NAME_MIN_LENGTH;
  const canSubmit = trimmedName.length >= NAME_MIN_LENGTH && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(undefined);
    lastErrorDetail.current = undefined;
    try {
      const result = await addApplication({
        name: trimmedName,
        description: description.trim() || null,
        ...(ag2AppId ? { ag2AppId } : {}),
      });
      onClose();
      navigate(paths.applicationById(workspaceId, result.id));
    } catch {
      setError(
        lastErrorDetail.current
          ? formatApiError(lastErrorDetail.current)
          : 'Failed to create application'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>Create Application</Modal.Header>
        <Modal.CloseButton />
        <Modal.Body>
          <FormControl isInvalid={nameIsTooShort} isRequired>
            <FormControl.Label>Name</FormControl.Label>
            <Input
              maxLength={NAME_MAX_LENGTH}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void handleSubmit();
                }
              }}
              placeholder="Application name"
              value={name}
            />
            {nameIsTooShort && (
              <FormControl.ErrorText>
                Name must be at least {NAME_MIN_LENGTH} characters
              </FormControl.ErrorText>
            )}
          </FormControl>
          <FormControl mt={4}>
            <FormControl.Label>Description</FormControl.Label>
            <Textarea
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
              placeholder="Optional description"
              value={description}
            />
          </FormControl>
          <FormControl mt={4}>
            <FormControl.Label>AG 2.0 APP-ID</FormControl.Label>
            <Ag2AppIdSelect
              ag2ProductId={workspaceResponse?.enterpriseArchitectureProductId}
              isDisabled={isSubmitting || isWorkspaceLoading}
              onChange={setAg2AppId}
              placeholder="Select AG 2.0 application (optional)"
              selectedAg2AppId={ag2AppId}
              width="100%"
            />
          </FormControl>
          {error && (
            <Text color="error.500" fontSize="sm" mt={4}>
              {error}
            </Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <HorizontalStack spacing={2}>
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button isDisabled={!canSubmit} isLoading={isSubmitting} onClick={handleSubmit}>
              Create
            </Button>
          </HorizontalStack>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
